// ⚠️ #1522 — the console.warn calls in this file stay warn, deliberately.
//
// main.tsx runs captureConsoleIntegration at levels ["error"], so a warn
// produces no Sentry event. That is the point here: presence bumps and notification claims, both on timers
// fails on a timer or a shim, nothing she did depends on it, and nothing is
// waiting on the result. The repo's rule is "warn means expected and handled,
// error means a human should see it" — these are the warns that rule exists
// for, and converting them is the quota flood main.tsx's comment warns about.
//
// A failed WRITE she initiated does not belong here. If one is added to this
// file, report it with reportError.

import { supabase } from "@/integrations/supabase/client";

/**
 * Issue #803 — the client half of "toast when she's still in the app, push or
 * text when she isn't."
 *
 * #742 loads her Kroger cart from a 5-minute cron sweep and then tells her. Its
 * scope asked for a toast in the first case and it shipped push/SMS for both,
 * because approving a meal plan doesn't commit synchronously — Approve just
 * moves auto_commit_at to now() and the same sweep does the work later. There
 * is no synchronous moment on the client to hang a toast off, and this codebase
 * has no Supabase Realtime subscription anywhere to learn about the commit from.
 *
 * So the server decides and leaves the notification for us:
 *
 *   1. This module bumps `user_presence` every PRESENCE_HEARTBEAT_MS while the
 *      app is visible. That is the only thing that tells the server she's here.
 *   2. run-scheduled-tasks checks the heartbeat at cart-load time. Stale, and
 *      it sends push/SMS exactly as today — none of this runs.
 *   3. Fresh, and it inserts an `in_app_notifications` row instead of sending,
 *      with a fallback deadline ~6 minutes out.
 *   4. The same tick as the heartbeat polls for that row and CLAIMS it before
 *      showing anything.
 *   5. If we never claim it, the cron claims it after the deadline and sends
 *      the push/text after all.
 *
 * ⚠️ The claim is what makes this safe, and it has to happen BEFORE the toast:
 *
 *     update ... set delivered_at = now() where id = $1 and delivered_at is null
 *
 * A single-row conditional UPDATE is atomic in Postgres, so this and the cron's
 * mirror claim cannot both succeed. Toast only if the claim returned a row and
 * she can never get both a toast and a text for one cart load. Claim-then-toast
 * (rather than toast-then-claim) also means the honest failure is a duplicate
 * *nothing* — a claimed row whose toast didn't render — which the next cart load
 * makes visible, instead of a phantom text minutes after she read the toast.
 */

/**
 * How often the app says "I'm here". The server's presence window is 150s
 * (PRESENCE_WINDOW_MS in supabase/functions/run-scheduled-tasks/inAppHandoff.ts)
 * — 2.5 ticks, so one dropped beat doesn't cost her the toast.
 *
 * ⚠️ Raising this past ~60s means raising the server window too, or she starts
 * getting pushes while sitting in the app. The relationship is pinned by
 * `inAppHandoff.test.ts`.
 */
export const PRESENCE_HEARTBEAT_MS = 60_000;

export interface PendingInAppNotification {
  id: string;
  kind: string;
  toast_title: string;
  toast_body: string;
  action_target: string | null;
}

/**
 * "I'm looking at the app."
 *
 * Fire-and-forget in spirit but NOT in error handling — a heartbeat that has
 * been failing for a week is the difference between a toast and a text, and
 * that is worth a console line. Same lean as analytics.ts: it must never break
 * anything, so every path swallows.
 *
 * Returns whether the bump landed, so the tick below can skip its poll when
 * presence is clearly broken (the server won't have deferred anything anyway).
 */
export async function bumpPresence(): Promise<boolean> {
  try {
    const { error } = await supabase.rpc("bump_user_presence" as never, {} as never);
    if (error) {
      // Includes "function does not exist" until the #803 SQL has been run.
      console.warn("bump_user_presence failed:", error.message);
      return false;
    }
    return true;
  } catch (e) {
    console.warn("bump_user_presence threw:", e);
    return false;
  }
}

/** Notifications the server left for this app to deliver, oldest first. */
export async function fetchPendingInAppNotifications(
  userId: string,
): Promise<PendingInAppNotification[]> {
  try {
    const { data, error } = await (supabase as any)
      .from("in_app_notifications")
      .select("id, kind, toast_title, toast_body, action_target")
      .eq("user_id", userId)
      .is("delivered_at", null)
      .is("fallback_sent_at", null)
      .order("created_at", { ascending: true })
      .limit(5);
    if (error) {
      console.warn("in_app_notifications poll failed:", error.message);
      return [];
    }
    return (data ?? []) as PendingInAppNotification[];
  } catch (e) {
    console.warn("in_app_notifications poll threw:", e);
    return [];
  }
}

/**
 * Take ownership of one notification. True means this device is now the one
 * telling her, and nothing else will.
 *
 * `.select("id")` is load-bearing twice over. It is house rule 16 — an
 * RLS-refused UPDATE returns 200 with `error === null` and no rows, so the
 * returned row is the only proof — and it is also how the race is decided: no
 * row back means the cron (or another device) claimed it first, and showing the
 * toast anyway would be the double-notification this whole design exists to
 * prevent.
 */
export async function claimInAppNotification(id: string): Promise<boolean> {
  try {
    const { data, error } = await (supabase as any)
      .from("in_app_notifications")
      .update({ delivered_at: new Date().toISOString() })
      .eq("id", id)
      .is("delivered_at", null)
      .is("fallback_sent_at", null)
      .select("id");
    if (error) {
      console.warn("in_app_notification claim failed:", error.message);
      return false;
    }
    return Array.isArray(data) && data.length > 0;
  } catch (e) {
    console.warn("in_app_notification claim threw:", e);
    return false;
  }
}

export interface InAppNotificationTickDeps {
  bump: () => Promise<boolean>;
  fetchPending: (userId: string) => Promise<PendingInAppNotification[]>;
  claim: (id: string) => Promise<boolean>;
  show: (notification: PendingInAppNotification) => void;
  /**
   * Ids this session is already mid-claim on. The database claim settles the
   * cross-device race; this settles the in-process one, where two overlapping
   * ticks (an interval and a resume, say) both fetch the same row before
   * either has claimed it. Without it she'd see the same toast twice from one
   * device, which no amount of atomicity in Postgres can prevent.
   */
  inFlight: Set<string>;
}

/**
 * One tick: say we're here, then deliver anything waiting.
 *
 * Pure orchestration over injected dependencies so the ordering guarantees —
 * claim before show, never show on a lost claim, never show the same id twice —
 * are testable without a database. Returns how many toasts were shown.
 */
export async function runInAppNotificationTick(
  userId: string,
  deps: InAppNotificationTickDeps,
): Promise<number> {
  const bumped = await deps.bump();
  // Presence is the reason a row would exist at all. If the bump failed, the
  // server saw us as away and sent on push/SMS, so there is nothing to poll for
  // and one failed request is enough for this tick.
  if (!bumped) return 0;

  const pending = await deps.fetchPending(userId);
  let shown = 0;

  for (const notification of pending) {
    if (deps.inFlight.has(notification.id)) continue;
    deps.inFlight.add(notification.id);
    try {
      const won = await deps.claim(notification.id);
      if (!won) continue;
      deps.show(notification);
      shown += 1;
    } finally {
      // Deliberately NOT kept forever on success: the row is stamped
      // delivered_at, so it can never come back from fetchPending, and holding
      // ids for the life of the session would be an unbounded leak on a tab
      // left open for days. On failure, dropping it lets the next tick retry.
      deps.inFlight.delete(notification.id);
    }
  }

  return shown;
}
